import moment from 'moment-timezone';

export const getStartDateAndClosingTime = (
  date: Date,
  openingTimes: [number, number], // [hours, minutes]
  closingTimes: [number, number], // [hours, minutes] (optional)
  timeZone: string,
) => {
  const timeZoneDate = new Date(
    date.toLocaleString('en-US', {timeZone: timeZone}),
  );

  if (!closingTimes) {
    // Assuming closing times are optional
    // Handle standard case where opening and closing are on the same day
    const openingTime = new Date(
      timeZoneDate.getFullYear(),
      timeZoneDate.getMonth(),
      timeZoneDate.getDate(),
      openingTimes[0],
      openingTimes[1],
      0,
    );

    const closingTime = new Date(
      openingTime.getFullYear(),
      openingTime.getMonth(),
      openingTime.getDate(),
      closingTimes[0],
      closingTimes[1],
      0,
    );

    return {openingTime, closingTime};
  } else {
    // Handle case where opening extends to next day or closing is early next morning

    const [openingHours, openingMinutes] = openingTimes;
    const [closingHours, closingMinutes] = closingTimes;
    const openingDate = new Date(
      timeZoneDate.getFullYear(),
      timeZoneDate.getMonth(),
      timeZoneDate.getDate(),
      openingHours,
      openingMinutes,
      0,
    );
    let closingDate = new Date(openingDate);
    const closingDayOffset = closingHours >= openingHours ? 0 : 1;
    closingDate.setDate(closingDate.getDate() + closingDayOffset);
    closingDate.setHours(closingHours);
    closingDate.setMinutes(closingMinutes);
    closingDate.setSeconds(0);

    return {openingTime: openingDate, closingTime: closingDate};
  }
};

export const getStartDateByDateAndOpeningTime = (
  date: Date,
  openingTimes: [number, number],
  timeZone: string,
) => {
  const timeZoneDate = new Date(
    date.toLocaleString('en-US', {timeZone: timeZone}),
  );
  const [hours, minutes] = openingTimes;
  if (
    timeZoneDate.getHours() < hours ||
    (timeZoneDate.getHours() === hours && timeZoneDate.getMinutes() < minutes)
  ) {
    const dateWithOpeningTime = new Date(
      timeZoneDate.getFullYear(),
      timeZoneDate.getMonth(),
      timeZoneDate.getDate(),
      hours,
      minutes,
    );
    return dateWithOpeningTime;
  } else {
    const dateWithOpeningTime = new Date(
      timeZoneDate.getFullYear(),
      timeZoneDate.getMonth(),
      timeZoneDate.getDate(),
      hours,
      minutes,
    );
    return dateWithOpeningTime;
  }
};

const createDateInTimezone = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string,
): Date => {
  // Note: month is 0-based, like Date in JavaScript (0 = January, 1 = February, etc.)
  const formattedDate = `${year}-${month + 1}-${day} ${hour}:${minute}`;

  return moment.tz(formattedDate, 'YYYY-MM-DD HH:mm', timezone).toDate();
};

export const getOpeningDatesByDatesAndOpeningTimes = (
  startDate: Date,
  endDate: Date,
  openingTimes: [number, number],
  closingTimes: [number, number],
  timeZone: string,
): {
  openingDate: Date;
  closingDate: Date;
} => {
  const timeZoneStartDate = new Date(
    startDate.toLocaleString('en-US', {timeZone: timeZone}),
  );
  const timeZoneEndDate = new Date(
    endDate.toLocaleString('en-US', {timeZone: timeZone}),
  );

  const [openingHours, openingMinutes] = openingTimes;
  const [closingHours, closingMinutes] = closingTimes;

  // Determine opening date based on opening time
  const openingDate = getStartDateByDateAndOpeningTime(
    timeZoneStartDate,
    openingTimes,
    timeZone,
  );

  // Create closing date using createDateInTimezone and adjust if necessary
  const closingDate = createDateInTimezone(
    timeZoneEndDate.getFullYear(),
    timeZoneEndDate.getMonth(),
    timeZoneEndDate.getDate(),
    closingHours,
    closingMinutes,
    timeZone,
  );

  // Handle cases where closing time extends to the next day:
  if (
    closingHours < openingHours ||
    (closingHours === openingHours && closingMinutes < openingMinutes)
  ) {
    closingDate.setDate(closingDate.getDate() + 1);
  }

  return {
    openingDate,
    closingDate,
  };
};
